import Divider from "../../../assets/new-design/Divider/divider.svg";
import { Link } from "@tanstack/react-router";

export type MenuItemDef = {
  label: string;
  icon: string;
  isLink: boolean;
  href?: string;
};

export type SidebarMenuProps = {
  title?: string;
  sections: readonly (readonly MenuItemDef[])[];
  activeItem: string;
  onActiveItemClick?: () => void;
};

function MenuItemComponent({
  item,
  isActive,
  onActiveItemClick,
}: {
  item: MenuItemDef;
  isActive: boolean;
  onActiveItemClick?: () => void;
}) {
  const isNavigable = item.isLink && !isActive && !!item.href;
  const isActiveClickable = isActive && !!onActiveItemClick;
  const activeClass = isActive ? "bg-npc-background-lightblue " : "";
  const cursorClass = item.isLink ? "cursor-pointer " : "";
  const className = `${activeClass}${cursorClass}content-stretch flex gap-sds-size-space-300 items-center overflow-clip px-sds-size-space-400 py-sds-size-space-300 relative shrink-0 w-[224px]`;

  if (isNavigable) {
    return (
      <Link className={className} data-name=".MenuItem" to={item.href}>
        <div className="relative shrink-0 size-[24px]" data-name="Icon">
          <img
            alt=""
            className="absolute block inset-0 max-w-none size-full"
            src={item.icon}
          />
        </div>
        <div
          className="content-stretch flex flex-[1_0_0] flex-col items-start justify-center min-w-px relative"
          data-name="Body"
        >
          <div
            className="content-stretch flex gap-sds-size-space-100 items-start relative shrink-0 w-full"
            data-name="Label"
          >
            <p className="[word-break:break-word] font-['Noto_Sans_JP:Regular'] font-normal leading-[1.4] relative shrink-0 text-[14px] text-npc-text-black text-left whitespace-nowrap">
              {item.label}
            </p>
          </div>
        </div>
      </Link>
    );
  }

  const handleActiveClick = isActiveClickable ? onActiveItemClick : undefined;
  const handleActiveKeyDown = isActiveClickable
    ? (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onActiveItemClick?.();
        }
      }
    : undefined;

  return (
    <div
      className={className}
      data-name=".MenuItem"
      onClick={handleActiveClick}
      onKeyDown={handleActiveKeyDown}
      role={isActiveClickable ? "button" : undefined}
      tabIndex={isActiveClickable ? 0 : undefined}
    >
      <div className="relative shrink-0 size-[24px]" data-name="Icon">
        <img
          alt=""
          className="absolute block inset-0 max-w-none size-full"
          src={item.icon}
        />
      </div>
      <div
        className="content-stretch flex flex-[1_0_0] flex-col items-start justify-center min-w-px relative"
        data-name="Body"
      >
        <div
          className="content-stretch flex gap-sds-size-space-100 items-start relative shrink-0 w-full"
          data-name="Label"
        >
          <p className="[word-break:break-word] font-['Noto_Sans_JP:Regular'] font-normal leading-[1.4] relative shrink-0 text-[14px] text-npc-text-black text-left whitespace-nowrap">
            {item.label}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SidebarMenu({
  title,
  sections,
  activeItem,
  onActiveItemClick,
}: SidebarMenuProps) {
  return (
    <div
      className="bg-npc-background-white content-stretch flex flex-col items-start p-sds-size-space-200 relative rounded-sds-radius-200 shrink-0 w-[240px]"
      data-name="Menu"
    >
      {title && (
        <div
          className="content-stretch flex flex-col items-start overflow-clip pb-sds-size-space-100 pt-sds-size-space-200 px-sds-size-space-400 relative shrink-0 w-[224px]"
          data-name="Menu Header"
        >
          <p className="[word-break:break-word] font-['Noto_Sans_JP:Bold'] font-bold leading-[1.4] relative shrink-0 text-[16px] text-npc-text-black w-full">
            {title}
          </p>
        </div>
      )}
      <div
        className="content-stretch flex flex-col items-start relative shrink-0 w-full"
        data-name="Menu Section"
      >
        {sections.map((sectionItems, sectionIndex) => (
          <div key={sectionIndex} className="contents">
            <div
              className="h-[16px] relative shrink-0 w-full"
              data-name="Divider"
            >
              <img
                alt=""
                className="absolute block inset-0 max-w-none size-full"
                src={Divider}
              />
            </div>
            {sectionItems.map((item) => (
              <MenuItemComponent
                key={item.label}
                item={item}
                isActive={item.label === activeItem}
                onActiveItemClick={onActiveItemClick}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
